import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import {
    Box,
    Divider,
    TextField,
    FormControlLabel,
    FormGroup,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Checkbox } from 'common/components/Checkbox';
import { SectionTitle } from 'common/components/SectionTitle';
import { Button } from 'common/components/buttons/Button';
import { useSnackbarContext } from 'common/context';
import debounce from 'lodash/debounce';

import {
    IEmployeeType,
    useEmployeesAndBudgetsQuery,
    useInvitationsCreateMutation,
} from 'graphql/types';

type TEmployee = Pick<
    IEmployeeType,
    | 'id'
    | 'firstName'
    | 'lastNamePrefix'
    | 'lastName'
    | 'email'
    | 'personnelNumber'
>;

interface IFormValues {
    searchInput: string;
    employee?: TEmployee;
    budgetIds: string[];
}

const BaseInviteExistingEmployeeForm = (
    props: React.HTMLAttributes<HTMLFormElement>
) => {
    const { setSnackbarState } = useSnackbarContext();
    const { t } = useTranslation();

    const { data, loading, refetch } = useEmployeesAndBudgetsQuery({
        fetchPolicy: 'no-cache',
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const [createInvitations] = useInvitationsCreateMutation({
        onCompleted: () => {
            setSnackbarState({
                message: t('messages.invitationSent'),
                variant: 'success',
            });
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const { employees, company } = data || {};

    const getEmployeeOptions = () => {
        return employees?.objects ?? [];
    };

    const getEmployeeDisplayValue = (employee: TEmployee) => {
        if (!employee.lastNamePrefix)
            return `${employee.firstName} ${employee.lastName}`;

        return `${employee.firstName} ${employee.lastNamePrefix} ${employee.lastName}`;
    };

    const autoCompleteFilterOptions = createFilterOptions<TEmployee>({
        // When searching the autocomplete, append email and personnelNumber to the search string
        // so that those become searchable as well
        stringify: (option) =>
            getEmployeeDisplayValue(option) +
            option.email +
            option.personnelNumber,
    });

    const initialValues: IFormValues = {
        searchInput: '',
        employee: undefined,
        budgetIds: [],
    };

    const formik = useFormik<IFormValues>({
        initialValues,
        validationSchema: Yup.object().shape({
            employee: Yup.object().required(),
            budgetIds: Yup.array().min(1, t('validators.budget.min')),
        }),
        onSubmit: (values, { resetForm }) => {
            if (!values.employee) return;

            createInvitations({
                variables: {
                    employeeId: values.employee.id,
                    budgetIds: values.budgetIds,
                },
            });

            resetForm();
        },
    });

    const { values, handleSubmit, setFieldValue } = formik;

    const handleSearchInputChange = useCallback(
        debounce((value: string) => {
            refetch({
                search: value,
            });
        }, 300),
        []
    );

    useEffect(() => {
        if (!company) return;

        if (company.budgets.length === 1) {
            setFieldValue('budgetIds', [company.budgets[0].id]);
        }
        // Make sure the budget is set after form reset, by adding values.employee as a dependecy,
    }, [company, values.employee]);

    useEffect(() => {
        handleSearchInputChange(values.searchInput);
    }, [values.searchInput]);

    const handleBudgetClick = (id: string) => {
        if (values.budgetIds.includes(id)) {
            setFieldValue(
                'budgetIds',
                values.budgetIds.filter((budget) => budget !== id)
            );
        } else {
            setFieldValue('budgetIds', [...values.budgetIds, id]);
        }
    };

    const buttonDisabled = !values.budgetIds.length || !values.employee;

    return (
        <form {...props} onSubmit={handleSubmit}>
            <Box maxWidth={400} mb={5}>
                <SectionTitle>{t('employee.searchHeader')}</SectionTitle>
                <Autocomplete
                    autoHighlight
                    disableClearable
                    filterOptions={autoCompleteFilterOptions}
                    getOptionLabel={(option) => getEmployeeDisplayValue(option)}
                    getOptionSelected={(option, value) =>
                        option.id === value.id
                    }
                    inputValue={values.searchInput}
                    loading={loading}
                    options={getEmployeeOptions()}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            InputProps={{
                                ...params.InputProps,
                                type: 'search',
                            }}
                            label={t('employee.search')}
                            variant="outlined"
                        />
                    )}
                    value={values.employee}
                    onChange={(_e, value) => {
                        if (value && typeof value !== 'string') {
                            setFieldValue('employee', value);
                        }
                    }}
                    onInputChange={(_e, value) =>
                        setFieldValue('searchInput', value)
                    }
                />
            </Box>
            {company && company.budgets.length > 1 && (
                <Box mb={6}>
                    <SectionTitle>
                        {t('employee.detail.chooseBudget')}
                    </SectionTitle>
                    <FormGroup>
                        {company.budgets?.map((budget) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={() =>
                                            handleBudgetClick(budget.id)
                                        }
                                    />
                                }
                                key={budget.id}
                                label={budget.name}
                            />
                        ))}
                    </FormGroup>
                </Box>
            )}
            <Divider />
            <Box display="flex" justifyContent="flex-end" mt={6}>
                <Button disabled={buttonDisabled} type="submit">
                    {t('employee.buttonInvite')}
                </Button>
            </Box>
        </form>
    );
};

export const InviteExistingEmployeeForm = styled(
    BaseInviteExistingEmployeeForm
)``;
